import React, { useState, useEffect } from 'react';
import { useParams, useNavigate, useLocation, Link } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { useTheme } from '../context/ThemeContext';
import { useNavigation } from '../context/NavigationContext';
import { ArrowLeft, Heart, ThumbsDown, Repeat2, Share2, Send, ChevronDown, Calendar, Users, UserCheck, Bell, MessageSquare, Edit3, MoreVertical, Pin, Trash, LogOut } from 'lucide-react';
import PostDetailNavbar from '../components/desktop/PostDetailNavbar';
import { getLegacyPostUrl, getCommunityPostUrl, getForumPostUrl } from '../utils/postUrls';
import AnimationLayer from '../components/desktop/AnimationLayer';
import { getThemeAnimations, type ThemeAnimations } from '@theme/animation-config';
import { useScale, scaleW, scaleH } from '../hooks/useScale';
import { useDynamicBackground } from '../hooks/useDynamicBackground';
import LoadingScreen from '../components/desktop/LoadingScreen';
import { canModeratePost, getEditorType, getPinnerType } from '../utils/permissions';
import { normalizeLikeCounts } from '../components/shared/hooks/useFeedData';
import { EditPostModal } from '../components/desktop/EditPostModal';
import { PostBadges } from '../components/desktop/PostBadges';

import { postEventBus } from '../lib/postEventBus';
import { usePostSync } from '../hooks/usePostSync';
import { commentEventBus } from '../lib/commentEventBus';
import { useCommentSync } from '../hooks/useCommentSync';
import { getBackground, getCardBackground, calculateNavbarBg } from '../utils/backgroundHelpers';
import resolveAvatar from '../lib/resolveAvatar';
import { getCachedTheme } from '../utils/themeCache';

import { fetchWithAuth } from '../lib/fetchWithAuth';
type User = {
  id: number | string;
  username: string;
  displayName?: string;
  avatar?: string;
};

type Comment = {
  id: string;
  author: string;
  authorId: string;
  authorUsername?: string;
  authorAvatar?: string;
  content: string;
  timestamp: string;
  likes: number;
  dislikes: number;
  liked: boolean;
  disliked: boolean;
};

type Post = {
  id: string;
  author: string;
  authorId: string;
  authorUsername?: string;
  authorAvatar?: string;
  title?: string;
  slug?: string;
  content: string;
  mediaUrl?: string;
  imageUrl?: string;
  aboveContent?: boolean;
  belowContent?: boolean;
  tags?: string[];
  likes: number;
  dislikes: number;
  disliked?: boolean;
  comments: number;
  shares: number;
  timestamp: string;
  liked: boolean;
  createdAt: string;
};

export default function PostDetail() {
  const { username, slug } = useParams<{ username: string; slug: string }>();
  const navigate = useNavigate();
  const location = useLocation();
  const { user: authUser, loading: authLoading, logout, setShowLoginModal } = useAuth();
  const { theme, currentPreset, siteAnimations, siteCustomization } = useTheme();
  const { setCurrentTheme } = useNavigation();
  const scale = useScale();
  const backgroundHeight = useDynamicBackground();
  const [post, setPost] = useState<Post | null>(null);
  const [allComments, setAllComments] = useState<Comment[]>([]);
  const [displayedComments, setDisplayedComments] = useState<Comment[]>([]);
  const [commentPage, setCommentPage] = useState(1);
  const currentUser = authUser;
  const [newComment, setNewComment] = useState('');
  const [activeTab, setActiveTab] = useState<'all' | 'following' | 'friends'>('all');
  const [showShareModal, setShowShareModal] = useState(false);
  // Initialize animations based on navigation context to prevent flash
  const [themeAnimations, setThemeAnimations] = useState<ThemeAnimations | null>(() => {
    const navState = location.state as any;

    // If coming from profile, DON'T show any animations yet - keep loading screen up
    // This prevents showing wrong animations while the profile theme is being fetched
    if (navState?.themeContext?.type === 'profile') {
      return null;
    }

    // If coming from community, try to load cached community animations
    if (navState?.themeContext?.type === 'community' && navState?.themeContext?.communityId) {
      const cachedTheme = getCachedTheme('community', navState.themeContext.communityId);
      if (cachedTheme?.animations) {
        return cachedTheme.animations;
      }
    }

    // For site/feed: load from site theme cache (not user's profile theme)
    const siteTheme = getCachedTheme('site');
    if (siteTheme?.animations) {
      return siteTheme.animations;
    }

    // Final fallback: use site preset animations
    return getThemeAnimations(currentPreset);
  });
  const [loading, setLoading] = useState(true);
  // Only wait for theme loading if coming from a profile page
  const [themeLoading, setThemeLoading] = useState(() => {
    const navState = location.state as any;
    return navState?.themeContext?.type === 'profile';
  });
  const [showModerateMenu, setShowModerateMenu] = useState(false);
  const [editingPost, setEditingPost] = useState<Post | null>(null);
  const [showProfileMenu, setShowProfileMenu] = useState(false);
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [profileCustomization, setProfileCustomization] = useState<any>(null);

  // CONTEXT-AWARE THEMING: Register theme based on navigation context
  // STEP 1: Register from location.state if available (passed from Feed/Profile)
  useEffect(() => {
    const navState = location.state as any;
    if (navState?.themeContext?.colors) {
      setCurrentTheme({
        type: navState.themeContext.type,
        userId: navState.themeContext.userId,
        communityId: navState.themeContext.communityId,
        colors: navState.themeContext.colors
      });
          }
  }, [location.state, setCurrentTheme]);

  // STEP 2: Fetch author's profile customization for gradient settings and animations
  // Only use author's profile theme if navigating FROM their profile page
  useEffect(() => {
    const navState = location.state as any;
    const isFromProfile = navState?.themeContext?.type === 'profile';
    const isFromSite = navState?.themeContext?.type === 'site' || navState?.from === 'feed';

    if (post?.authorUsername && isFromProfile) {
      const fetchAuthorProfile = async () => {
        try {
          // FIRST: Try to load from cache (most up-to-date)
          const cachedProfileTheme = getCachedTheme('profile', post.authorId.toString());
          if (cachedProfileTheme?.animations) {
            // Cache hit! Use cached animations (most recent)
            setThemeAnimations(cachedProfileTheme.animations);
            // Don't set themeLoading = false yet - wait for API fetch to complete
            // to ensure we have full customization data before rendering
          }

          const response = await fetchWithAuth(`/api/profile/username/${post.authorUsername}`, {
            credentials: 'include'
          });

          if (!response.ok) {
            // If fetch fails but we have cache, we're good
            if (cachedProfileTheme?.animations) {
              return;
            }
            setThemeLoading(false);
            return;
          }

          const data = await response.json();
          const customization = data.user?.profileCustomization;

          if (customization) {
            // Store the FULL customization object for gradient settings
            setProfileCustomization(customization);

            // Profile uses 'presetTheme' field, not 'preset'
            const profilePreset = customization.presetTheme || customization.preset || 'ocean-turtle';

            // Only set animations if we didn't already get them from cache
            if (!cachedProfileTheme?.animations) {
              let newAnimations;
              if (customization.animations) {
                newAnimations = customization.animations;
              } else {
                // Fallback to preset animations if no custom animations saved
                newAnimations = getThemeAnimations(profilePreset);
              }
              setThemeAnimations(newAnimations);
            }

            // Update NavigationContext with profile colors
            if (customization.primaryColor) {
              const colors = {
                primaryColor: customization.primaryColor,
                secondaryColor: customization.secondaryColor || customization.primaryColor,
                textColor: customization.textColor || '#ffffff',
                accentColor: customization.accentColor || customization.primaryColor
              };

              setCurrentTheme({
                type: 'profile',
                userId: post.authorId.toString(),
                colors
              });
            }
          }
        } catch (error) {
          // Silently fail - will use default theme
        } finally {
          setThemeLoading(false);
        }
      };

      fetchAuthorProfile();
    } else if (isFromSite) {
      // Coming from feed - use site theme, not author's profile theme
      setProfileCustomization(null);
      setThemeAnimations(getThemeAnimations(currentPreset));
      setThemeLoading(false); // No theme fetch needed
    } else {
      // No navigation state - set theme loading to false
      setThemeLoading(false);
    }
  }, [post?.authorUsername, post?.authorId, location.state, setCurrentTheme, currentPreset]);

  // Update animations when theme preset changes, but only if no profile customization exists
  // (profile animations should take precedence over site theme animations)
  useEffect(() => {
    if (!profileCustomization) {
      setThemeAnimations(getThemeAnimations(currentPreset));
    }
  }, [currentPreset, profileCustomization]);

  // Sync animations from ThemeContext when not showing profile customization
  useEffect(() => {
    const navState = location.state as any;
    const isFromProfile = navState?.themeContext?.type === 'profile';
    const isFromCommunity = navState?.themeContext?.type === 'community';

    // Only use siteAnimations if NOT coming from profile or community
    if (!isFromProfile && !isFromCommunity && !profileCustomization && siteAnimations) {
      setThemeAnimations(siteAnimations);
    }
  }, [siteAnimations, location.state, profileCustomization]);

  // Listen for theme updates from customizer for animations only
  useEffect(() => {
    const handleThemeUpdate = (event: any) => {
      const { animations } = event.detail;

      // Only update if not showing profile customization
      if (!profileCustomization && animations) {
        const fixedAnimations = { ...animations };

        // Fix incomplete bubbles structure
        if (fixedAnimations.bubbles && typeof fixedAnimations.bubbles === 'object') {
          if (!fixedAnimations.bubbles.id || !fixedAnimations.bubbles.type || !fixedAnimations.bubbles.count) {
            fixedAnimations.bubbles = fixedAnimations.bubbles.enabled
              ? {
                  id: 'bubbles',
                  type: 'rising',
                  enabled: true,
                  count: 25,
                  speed: 0.8,
                  size: { min: 15, max: 35 },
                }
              : null;
          }
        }

        // Basic structure validation
        const hasValidStructure =
          fixedAnimations.characters && Array.isArray(fixedAnimations.characters) &&
          fixedAnimations.decorations && Array.isArray(fixedAnimations.decorations) &&
          fixedAnimations.characters.length > 0 &&
          fixedAnimations.characters[0]?.id;

        if (hasValidStructure) {
          setThemeAnimations(fixedAnimations);
        }
      }
    };

    window.addEventListener('siteThemeUpdated', handleThemeUpdate);
    return () => window.removeEventListener('siteThemeUpdated', handleThemeUpdate);
  }, [profileCustomization]);

  // Notifications
  const [notifications, setNotifications] = useState<any[]>([]);
  const [unreadCount, setUnreadCount] = useState(0);
  const [showNotifications, setShowNotifications] = useState(false);

  const COMMENTS_PER_PAGE = 25;

  // CONTEXT-AWARE LOCAL THEME: Use navigation state colors if available, otherwise ThemeContext
  const localTheme = React.useMemo(() => {
    const navState = location.state as any;

    // Check if navigation state has theme colors (from Feed/Profile/Community navigation)
    if (navState?.themeContext?.colors) {
      const navColors = navState.themeContext.colors;
      
      // Calculate cardBg from navigation state primary color
      const navCardBg = calculateNavbarBg(navColors.primaryColor, 92);
      const navNavbarBg = calculateNavbarBg(navColors.primaryColor, 92);

      return {
        ...theme, // Keep other theme properties (animations, etc.)
        textColor: navColors.textColor || '#ffffff',
        accentColor: navColors.accentColor || navColors.primaryColor,
        primaryColor: navColors.primaryColor,
        secondaryColor: navColors.secondaryColor || navColors.primaryColor,
        // Calculate card/navbar backgrounds from navigation state colors
        cardBg: navCardBg,
        navbarBg: navNavbarBg,
        tabBarBg: navNavbarBg,
        cardGradientColor1: navColors.primaryColor,
        cardGradientColor2: navColors.secondaryColor || navColors.primaryColor,
        cardGradientEnabled: theme.cardGradientEnabled,
        appBg: theme.appBg,
      };
    }

    // No navigation state - use ThemeContext (site theme or direct link)
        return {
      ...theme,
      textColor: theme.textColor!,
      accentColor: theme.accentColor!,
      primaryColor: theme.primaryColor!,
      secondaryColor: theme.secondaryColor!,
      cardBg: theme.cardBg,
      cardGradientColor1: theme.cardGradientColor1,
      cardGradientColor2: theme.cardGradientColor2,
      cardGradientEnabled: theme.cardGradientEnabled,
      appBg: theme.appBg,
    };
  }, [theme, location.state]);

  const themeColors = {
    navbarBg: calculateNavbarBg(localTheme.primaryColor!, 92),
    accentColor: localTheme.accentColor!,
    textColor: localTheme.textColor!,
    tabBarBg: calculateNavbarBg(localTheme.primaryColor!, 92),
    gradientFrom: localTheme.primaryColor!,
    gradientTo: localTheme.secondaryColor!
  };

  // Listen for post updates from other pages/components
  usePostSync({
    onLike: (data) => {
      if (post && post.id === data.postId) {
        setPost(prev => prev ? { ...prev, liked: data.liked, likes: data.likes } : null);
      }
    },
    onDislike: (data) => {
      if (post && post.id === data.postId) {
        setPost(prev => prev ? { ...prev, disliked: data.disliked, dislikes: data.dislikes } : null);
      }
    },
    postIds: post ? [post.id] : undefined
  });

  // Listen for comment updates from other pages/components
  useCommentSync({
    onLike: (data) => {
      setDisplayedComments(prev => prev.map(c =>
        c.id === data.commentId.toString()
          ? { ...c, liked: data.liked, likes: data.likes }
          : c
      ));
    },
    onDislike: (data) => {
      setDisplayedComments(prev => prev.map(c =>
        c.id === data.commentId.toString()
          ? { ...c, disliked: data.disliked, dislikes: data.dislikes }
          : c
      ));
    },
  });

  const formatTimestamp = (timestamp: string) => {
    const date = new Date(timestamp);
    return date.toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'numeric',
      day: 'numeric',
      hour: 'numeric',
      minute: '2-digit',
      hour12: true
    });
  };

  const handleLogout = async () => {
    logout();
  };

  useEffect(() => {
    async function loadData() {
      // Wait for auth to finish loading
      if (authLoading) {
        return;
      }

      try {
        // Set auth state based on authUser
        if (authUser) {
          setIsAuthenticated(true);
          setShowLoginModal(false);
          // Site theme is already loaded by ThemeContext
        } else {
          setIsAuthenticated(false);
          setShowLoginModal(true);
        }

        // Fetch post data by username and slug (no auth required)
        const postResponse = await fetchWithAuth(`/api/posts/profile/${username}/${slug}`, {
          credentials: 'include'
        });

        if (postResponse.ok) {
          const postData = await postResponse.json();
          // Transform post data to resolve avatar and normalize likes
          const transformedPost = normalizeLikeCounts({
            ...postData.post,
            authorAvatar: resolveAvatar(postData.post.authorAvatar || postData.post.author?.profilePicture || postData.post.author?.image)
          });
          setPost(transformedPost);

          // Fetch comments for this post (no auth required)
          const commentsResponse = await fetchWithAuth(`/api/posts/${postData.post.id}/comments`, {
            credentials: 'include'
          });

          if (commentsResponse.ok) {
            const commentsData = await commentsResponse.json();
            const comments = (commentsData.comments || commentsData || []).map((comment: any) => ({
              ...comment,
              authorAvatar: resolveAvatar(comment.authorAvatar || comment.author?.profilePicture || comment.author?.image)
            }));
            setAllComments(comments);
            setDisplayedComments(comments.slice(0, COMMENTS_PER_PAGE));
          }
        }
      } catch (error) {
      } finally {
        setLoading(false);
      }
    }

    loadData();
  }, [username, slug, authUser, authLoading]);

  // Fetch notifications on mount
  useEffect(() => {
    const fetchNotifications = async () => {
      try {
        const response = await fetchWithAuth('/api/notifications', {
          credentials: 'include'
        });
        if (response.ok) {
          const data = await response.json();
          setNotifications(data.notifications || []);
          setUnreadCount(data.unreadCount || 0);
        }
      } catch (error) {
      }
    };

    fetchNotifications();
  }, []);


  const loadMoreComments = () => {
    const nextPage = commentPage + 1;
    const startIndex = 0;
    const endIndex = nextPage * COMMENTS_PER_PAGE;
    setDisplayedComments(allComments.slice(startIndex, endIndex));
    setCommentPage(nextPage);
  };

  const handlePostComment = async () => {
    if (!currentUser) {
      setShowLoginModal(true);
      return;
    }
    if (!newComment.trim() || !post) return;

    try {
      const response = await fetchWithAuth(`/api/posts/${post.id}/comments`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ content: newComment.trim() })
      });

      if (response.ok) {
        const comment = await response.json();
        // Ensure comment has all required properties with defaults
        const formattedComment = {
          ...comment,
          content: comment.content || newComment.trim(),
          author: comment.author || currentUser.displayName || currentUser.username,
          authorUsername: comment.authorUsername || currentUser.username,
          authorAvatar: resolveAvatar(comment.authorAvatar || currentUser.avatar),
          timestamp: comment.timestamp || new Date().toISOString(),
          likes: comment.likes || 0,
          dislikes: comment.dislikes || 0,
          liked: comment.liked || false,
          disliked: comment.disliked || false
        };
        setAllComments([formattedComment, ...allComments]);
        setDisplayedComments([formattedComment, ...displayedComments].slice(0, commentPage * COMMENTS_PER_PAGE));
        setNewComment('');
        if (post) {
          setPost({ ...post, comments: post.comments + 1 });
        }
      }
    } catch (error) {
    }
  };

  const likePost = async () => {
    if (!currentUser) {
      setShowLoginModal(true);
      return;
    }
    if (!post) return;

    // Capture initial state
    const wasLiked = post.liked;
    const initialLikes = post.likes;
    const newLiked = !wasLiked;
    const newLikes = wasLiked ? initialLikes - 1 : initialLikes + 1;

    // Optimistic update
    setPost({ ...post, liked: newLiked, likes: newLikes });

    // Emit local event for same-page sync
    postEventBus.emitLike(post.id, newLiked, newLikes);

    try {
      const response = await fetchWithAuth(`/api/posts/${post.id}/like`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
      });

      if (!response.ok) {
        // Revert to initial state
        setPost((prev) => prev ? { ...prev, liked: wasLiked, likes: initialLikes } : null);
        // Emit revert event
        postEventBus.emitLike(post.id, wasLiked, initialLikes);
      }
    } catch (error) {
      // Revert to initial state
      setPost((prev) => prev ? { ...prev, liked: wasLiked, likes: initialLikes } : null);
      // Emit revert event
      postEventBus.emitLike(post.id, wasLiked, initialLikes);
    }
  };

  const dislikePost = async () => {
    if (!currentUser) {
      setShowLoginModal(true);
      return;
    }
    if (!post) return;

    // Capture initial state
    const wasDisliked = post.disliked;
    const initialDislikes = post.dislikes || 0;
    const newDisliked = !wasDisliked;
    const newDislikes = wasDisliked ? initialDislikes - 1 : initialDislikes + 1;

    // Optimistic update
    setPost({
      ...post,
      disliked: newDisliked,
      dislikes: newDislikes
    });

    // Emit local event for same-page sync
    postEventBus.emitDislike(post.id, newDisliked, newDislikes);

    try {
      const response = await fetchWithAuth(`/api/posts/${post.id}/dislike`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
      });

      if (!response.ok) {
        // Revert to initial state
        setPost((prev) => prev ? { ...prev, disliked: wasDisliked, dislikes: initialDislikes } : null);
        // Emit revert event
        postEventBus.emitDislike(post.id, wasDisliked || false, initialDislikes);
      }
    } catch (error) {
      // Revert to initial state
      setPost((prev) => prev ? { ...prev, disliked: wasDisliked, dislikes: initialDislikes } : null);
      // Emit revert event
      postEventBus.emitDislike(post.id, wasDisliked || false, initialDislikes);
    }
  };

  const handleShare = () => {
    if (!post) return;

    const url = post.slug && post.authorUsername
      ? `${window.location.origin}${getLegacyPostUrl(post.authorUsername, post.slug)}`
      : `${window.location.origin}/user/${post.authorUsername}/${post.slug}`;

    navigator.clipboard.writeText(url).then(() => {
      alert('Link copied to clipboard!');
      setShowShareModal(false);
    }).catch(err => {
      alert('Failed to copy link');
    });
  };

  const getNotificationIcon = (type: string) => {
    switch (type) {
      case 'FOLLOW':
        return <Users size={20} style={{ color: localTheme.textColor }} />;
      case 'NEW_FRIEND':
        return <Heart size={20} style={{ color: localTheme.textColor }} />;
      case 'REPLY':
      case 'COMMUNITY_REPLY':
        return <MessageSquare size={20} style={{ color: localTheme.textColor }} />;
      case 'PROFILE_POST':
        return <Edit3 size={20} style={{ color: localTheme.textColor }} />;
      case 'LIKE':
        return <Heart size={20} style={{ color: localTheme.textColor }} />;
      default:
        return <Bell size={20} style={{ color: localTheme.textColor }} />;
    }
  };

  const getNotificationMessage = (notification: any) => {
    const actor = notification.actorName || 'Someone';
    switch (notification.type) {
      case 'FOLLOW':
        return `${actor} started following you`;
      case 'NEW_FRIEND':
        return `You and ${actor} are now friends!`;
      case 'REPLY':
        return `${actor} replied to your post`;
      case 'COMMUNITY_REPLY':
        return `${actor} replied to your community post`;
      case 'PROFILE_POST':
        return `${actor} posted on your profile`;
      case 'LIKE':
        return `${actor} liked your post`;
      default:
        return notification.content || 'New notification';
    }
  };

  const formatNotificationTime = (createdAt: string) => {
    const date = new Date(createdAt);
    const now = new Date();
    const diffMs = now.getTime() - date.getTime();
    const diffMins = Math.floor(diffMs / 60000);
    const diffHours = Math.floor(diffMins / 60);
    const diffDays = Math.floor(diffHours / 24);

    if (diffMins < 1) return 'Just now';
    if (diffMins < 60) return `${diffMins}m ago`;
    if (diffHours < 24) return `${diffHours}h ago`;
    if (diffDays < 7) return `${diffDays}d ago`;
    return date.toLocaleDateString();
  };

  const handleNotificationClick = (notification: any) => {
    setShowNotifications(false);

    switch (notification.type) {
      case 'FOLLOW':
      case 'NEW_FRIEND':
        if (notification.actorUsername) {
          navigate(`/u/${notification.actorUsername}`);
        } else if (notification.actorId) {
          navigate(`/user/${notification.actorId}`);
        }
        break;
      case 'REPLY':
      case 'COMMUNITY_REPLY':
      case 'LIKE':
      case 'DISLIKE':
      case 'REPOST':
        if (notification.postType === 'forum' && notification.communitySlug && notification.postSlug) {
          navigate(getForumPostUrl(notification.communitySlug, notification.postSlug));
        } else if (notification.postType === 'community' && notification.communitySlug && notification.postSlug) {
          navigate(getCommunityPostUrl(notification.communitySlug, notification.postSlug));
        } else if (notification.postType === 'legacy' && notification.postAuthorUsername && notification.postSlug) {
          navigate(getLegacyPostUrl(notification.postAuthorUsername, notification.postSlug));
        } else {
          navigate('/feed');
        }
        break;
      case 'PROFILE_POST':
        navigate('/user');
        break;
      case 'MESSAGE':
        navigate('/messages');
        break;
    }
  };

  const likeComment = async (commentId: string) => {
    if (!currentUser) {
      setShowLoginModal(true);
      return;
    }

    const comment = displayedComments.find(c => c.id === commentId);
    if (!comment) return;

    // Capture initial state
    const initialLiked = comment.liked || false;
    const initialLikes = comment.likes || 0;

    // Optimistic update
    const newLiked = !initialLiked;
    const newLikes = initialLiked ? initialLikes - 1 : initialLikes + 1;

    setDisplayedComments(prev => prev.map(c =>
      c.id === commentId
        ? { ...c, liked: newLiked, likes: newLikes }
        : c
    ));

    // Emit event to sync across components
    commentEventBus.emitLike(parseInt(commentId), newLiked, newLikes);

    try {
      const response = await fetchWithAuth(`/api/comments/${commentId}/like`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
      });

      if (!response.ok) {
        // Revert on error
        setDisplayedComments(prev => prev.map(c =>
          c.id === commentId
            ? { ...c, liked: initialLiked, likes: initialLikes }
            : c
        ));
        // Emit revert event
        commentEventBus.emitLike(parseInt(commentId), initialLiked, initialLikes);
      }
    } catch (error) {
      // Revert on error
      setDisplayedComments(prev => prev.map(c =>
        c.id === commentId
          ? { ...c, liked: initialLiked, likes: initialLikes }
          : c
      ));
      // Emit revert event
      commentEventBus.emitLike(parseInt(commentId), initialLiked, initialLikes);
    }
  };

  const dislikeComment = async (commentId: string) => {
    if (!currentUser) {
      setShowLoginModal(true);
      return;
    }

    const comment = displayedComments.find(c => c.id === commentId);
    if (!comment) return;

    // Capture initial state
    const initialDisliked = comment.disliked || false;
    const initialDislikes = comment.dislikes || 0;

    // Optimistic update
    const newDisliked = !initialDisliked;
    const newDislikes = initialDisliked ? initialDislikes - 1 : initialDislikes + 1;

    setDisplayedComments(prev => prev.map(c =>
      c.id === commentId
        ? { ...c, disliked: newDisliked, dislikes: newDislikes }
        : c
    ));

    // Emit event to sync across components
    commentEventBus.emitDislike(parseInt(commentId), newDisliked, newDislikes);

    try {
      const response = await fetchWithAuth(`/api/comments/${commentId}/dislike`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
      });

      if (!response.ok) {
        // Revert on error
        setDisplayedComments(prev => prev.map(c =>
          c.id === commentId
            ? { ...c, disliked: initialDisliked, dislikes: initialDislikes }
            : c
        ));
        // Emit revert event
        commentEventBus.emitDislike(parseInt(commentId), initialDisliked, initialDislikes);
      }
    } catch (error) {
      // Revert on error
      setDisplayedComments(prev => prev.map(c =>
        c.id === commentId
          ? { ...c, disliked: initialDisliked, dislikes: initialDislikes }
          : c
      ));
      // Emit revert event
      commentEventBus.emitDislike(parseInt(commentId), initialDisliked, initialDislikes);
    }
  };

  const handleEditPost = (post: Post) => {
    setEditingPost(post);
    setShowModerateMenu(false);
  };

  const handleSaveEdit = async (updatedData: { title?: string; content: string; mediaUrl?: string; removeMedia?: boolean }) => {
    if (!editingPost || !currentUser) return;

    const permUser = {
      id: typeof currentUser.id === 'string' ? parseInt(currentUser.id) : currentUser.id,
      username: currentUser.username,
      isAdmin: (currentUser as any).isSiteAdmin || (currentUser as any).isSuperAdmin || false,
      isModerator: (currentUser as any).isSiteModerator || false
    };

    const editorType = getEditorType(editingPost as any, permUser);
    const endpoint = `/api/moderation/posts/${editingPost.id}`;

    try {
      const response = await fetch(endpoint, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({
          ...updatedData,
          moderatorEditReason: editorType !== 'user' ? 'Post edited by moderator' : undefined
        })
      });

      if (response.ok) {
        setPost(prev => prev ? {
          ...prev,
          ...updatedData
        } : prev);
        setEditingPost(null);
      }
    } catch (error) {
      throw error;
    }
  };

  const handlePinPost = async (postToPin: Post) => {
    if (!currentUser) return;

    const permUser = {
      id: typeof currentUser.id === 'string' ? parseInt(currentUser.id) : currentUser.id,
      username: currentUser.username,
      isAdmin: (currentUser as any).isSiteAdmin || (currentUser as any).isSuperAdmin || false,
      isModerator: (currentUser as any).isSiteModerator || false
    };

    const endpoint = `/api/moderation/posts/${postToPin.id}/pin`;

    try {
      const response = await fetch(endpoint, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include'
      });

      if (response.ok) {
        const data = await response.json();
        setPost(prev => prev ? {
          ...prev,
          isPinned: data.pinned
        } as Post : prev);
        setShowModerateMenu(false);
      }
    } catch (error) {
    }
  };

  const handlePinToFeed = async (postToPin: Post) => {
    if (!currentUser || !(currentUser as any).isSuperAdmin) return;

    try {
      const response = await fetchWithAuth(`/api/moderation/posts/${postToPin.id}/pin-to-feed`, {
        method: 'POST',
        credentials: 'include'
      });

      if (response.ok) {
        const data = await response.json();
        setPost(prev => prev ? {
          ...prev,
          isPinnedToFeed: data.pinnedToFeed
        } as Post : prev);
        setShowModerateMenu(false);
      }
    } catch (error) {
    }
  };

  const handleDeletePost = async (postToDelete: Post) => {
    if (!confirm('Are you sure you want to delete this post?')) {
      return;
    }

    const endpoint = `/api/moderation/posts/${postToDelete.id}`;

    try {
      const response = await fetch(endpoint, {
        method: 'DELETE',
        credentials: 'include'
      });

      if (response.ok) {
        setShowModerateMenu(false);
        navigate(-1);
      } else {
        const data = await response.json();
        alert(data.error || 'Failed to delete post');
      }
    } catch (error) {
      alert('Network error. Please try again.');
    }
  };

  // Show loading screen until both data and theme are loaded
  // When coming from profile, MUST wait for profile theme to load to prevent flash
  const navState = location.state as any;
  const isFromProfile = navState?.themeContext?.type === 'profile';
  const needsProfileCustomization = isFromProfile && !profileCustomization;

  if (loading || themeLoading || needsProfileCustomization) {
    return <LoadingScreen />;
  }

  // Don't render AnimationLayer at all if animations are null (prevents dark flash)
  // ThemeAnimations should have characters/bubbles/decorations arrays
  const shouldShowAnimations = themeAnimations &&
    (themeAnimations.characters?.length > 0 ||
     themeAnimations.decorations?.length > 0 ||
     themeAnimations.bubbles !== null);

  return (
    <div className="relative" style={{ background: getBackground(localTheme as any, profileCustomization || {}, theme.appBg), minHeight: backgroundHeight }}>
      {shouldShowAnimations && (
        <AnimationLayer animations={themeAnimations} isMobile={false} bubbleColor={localTheme.accentColor} />
      )}

      <PostDetailNavbar
        onBack={() => navigate(-1)}
        onLogout={handleLogout}
        currentUser={currentUser}
        notifications={notifications}
        unreadCount={unreadCount}
        activeTab={activeTab}
        onTabChange={setActiveTab}
        theme={localTheme}
        showNotifications={showNotifications}
        setShowNotifications={setShowNotifications}
        showProfileMenu={showProfileMenu}
        setShowProfileMenu={setShowProfileMenu}
        onNotificationClick={handleNotificationClick}
      />

      {/* Wavy Line Separator */}
      <div className="w-full flex justify-center py-4">
        <svg width="80%" height="20" viewBox="0 0 800 20" preserveAspectRatio="none">
          <path
            d="M0,10 Q200,0 400,10 T800,10"
            stroke={localTheme.accentColor}
            strokeWidth="3"
            fill="none"
          />
        </svg>
      </div>

      {/* Main Content */}
      <div style={{
        width: '100%',
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        paddingBottom: `${scaleH(80, scale.h)}px`
      }}>
        {!post ? (
          <div className="flex items-center justify-center py-20">
            <div className="text-center">
              <h2 className="text-2xl font-bold mb-2" style={{ color: localTheme.textColor }}>Post not found</h2>
              <p style={{ color: localTheme.textColor + '99' }}>This post may have been deleted or doesn't exist.</p>
            </div>
          </div>
        ) : (
          <div style={{
            width: '100%',
            maxWidth: `${scaleW(1200, scale.w)}px`,
            padding: `0 ${scaleW(64, scale.w)}px`
          }}>
          <>
            {/* Post Card */}
            <div className="rounded-xl p-6 mb-6 backdrop-blur-sm border relative" style={{
              background: getCardBackground(localTheme as any, profileCustomization || {}, theme.cardBg),
              borderColor: localTheme.accentColor + '40'
            }}>
              {/* Post Badges */}
              {post && <PostBadges post={post as any} textColor={localTheme.textColor} accentColor={localTheme.accentColor} />}

              {/* Moderate Dropdown */}
              {currentUser && post && canModeratePost(post as any, {
                id: typeof currentUser.id === 'string' ? parseInt(currentUser.id) : currentUser.id,
                username: currentUser.username,
                isAdmin: (currentUser as any).isSiteAdmin || (currentUser as any).isSuperAdmin || false,
                isModerator: (currentUser as any).isSiteModerator || false
              }) && (
                <div className="absolute top-3 right-3" style={{ zIndex: 10 }}>
                  <button
                    onClick={() => setShowModerateMenu(!showModerateMenu)}
                    className="p-1.5 rounded-lg transition-all hover:opacity-80"
                    style={{
                      backgroundColor: localTheme.accentColor + '20',
                      color: localTheme.textColor
                    }}
                  >
                    <MoreVertical size={16} />
                  </button>

                  {showModerateMenu && (
                    <>
                      <div
                        className="absolute top-full right-0 mt-2 rounded-lg overflow-hidden shadow-2xl min-w-[140px]"
                        style={{
                          backgroundColor: themeColors.tabBarBg || 'rgba(30, 58, 95, 0.95)',
                          borderColor: localTheme.accentColor + '60',
                          border: '1px solid',
                          zIndex: 1001
                        }}
                      >
                        <button
                          onClick={() => handleEditPost(post)}
                          className="flex items-center gap-2 w-full px-4 py-3 text-left text-sm font-medium transition-all"
                          style={{
                            color: localTheme.textColor,
                            backgroundColor: 'transparent'
                          }}
                          onMouseEnter={(e) => {
                            e.currentTarget.style.backgroundColor = localTheme.accentColor + '20';
                          }}
                          onMouseLeave={(e) => {
                            e.currentTarget.style.backgroundColor = 'transparent';
                          }}
                        >
                          <Edit3 size={16} />
                          Edit
                        </button>
                        <button
                          onClick={() => handlePinPost(post)}
                          className="flex items-center gap-2 w-full px-4 py-3 text-left text-sm font-medium transition-all"
                          style={{
                            color: localTheme.textColor,
                            backgroundColor: 'transparent'
                          }}
                          onMouseEnter={(e) => {
                            e.currentTarget.style.backgroundColor = localTheme.accentColor + '20';
                          }}
                          onMouseLeave={(e) => {
                            e.currentTarget.style.backgroundColor = 'transparent';
                          }}
                        >
                          <Pin size={16} />
                          {(post as any).isPinned ? 'Unpin' : 'Pin'}
                        </button>
                        {(currentUser as any).isSuperAdmin && (
                          <button
                            onClick={() => handlePinToFeed(post)}
                            className="flex items-center gap-2 w-full px-4 py-3 text-left text-sm font-medium transition-all"
                            style={{ color: localTheme.textColor, backgroundColor: 'transparent' }}
                            onMouseEnter={(e) => e.currentTarget.style.backgroundColor = localTheme.accentColor + '20'}
                            onMouseLeave={(e) => e.currentTarget.style.backgroundColor = 'transparent'}
                          >
                            <Pin size={16} />
                            {(post as any).isPinnedToFeed ? 'Unpin from Feed' : 'Pin to Feed'}
                          </button>
                        )}
                        <button
                          onClick={() => handleDeletePost(post)}
                          className="flex items-center gap-2 w-full px-4 py-3 text-left text-sm font-medium transition-all"
                          style={{
                            color: '#ef4444',
                            backgroundColor: 'transparent'
                          }}
                          onMouseEnter={(e) => {
                            e.currentTarget.style.backgroundColor = '#ef444420';
                          }}
                          onMouseLeave={(e) => {
                            e.currentTarget.style.backgroundColor = 'transparent';
                          }}
                        >
                          <Trash size={16} />
                          Delete
                        </button>
                      </div>
                      <div
                        className="fixed inset-0"
                        style={{ zIndex: 90 }}
                        onClick={() => setShowModerateMenu(false)}
                      />
                    </>
                  )}
                </div>
              )}

              {/* Post Header */}
              <div className="flex items-center gap-3 mb-4">
                <Link to={post.authorUsername ? `/user/${post.authorUsername}` : '#'} className="w-12 h-12 rounded-full flex items-center justify-center cursor-pointer transition-opacity hover:opacity-80" style={{
                  background: `linear-gradient(to bottom right, ${localTheme.accentColor}, ${localTheme.accentColor}dd)`
                }}>
                  {post.authorAvatar ? (
                    <img src={post.authorAvatar} alt={post.author} className="w-full h-full rounded-full object-cover" />
                  ) : (
                    <span style={{ color: localTheme.textColor }}>{post.author?.[0] || 'U'}</span>
                  )}
                </Link>
            <div className="flex-1">
              <p className="font-semibold" style={{ color: localTheme.textColor }}>{post.author || 'Unknown'}</p>
              {post.authorUsername && (
                <p className="text-sm" style={{ color: localTheme.textColor + 'aa' }}>@{post.authorUsername}</p>
              )}
            </div>
            <span className="text-sm" style={{ color: localTheme.textColor + '99', position: 'relative', left: '-4.5rem' }}>{formatTimestamp(post.createdAt)}</span>
          </div>

          {/* Post Title */}
          {post.title && (
            <h2 className="text-2xl font-bold mb-3" style={{ color: localTheme.textColor }}>{post.title}</h2>
          )}

          {/* Post Media Above Content */}
          {(post.mediaUrl || post.imageUrl) && post.aboveContent && (
            <img src={post.mediaUrl || post.imageUrl} alt="Post media" className="w-full rounded-lg mb-4" />
          )}

          {/* Post Content */}
          <p className="mb-4 text-lg leading-relaxed" style={{ color: localTheme.textColor + 'ee' }}>{post.content}</p>

          {/* Post Media Below Content */}
          {(post.mediaUrl || post.imageUrl) && !post.aboveContent && (
            <img src={post.mediaUrl || post.imageUrl} alt="Post media" className="w-full rounded-lg mb-4" />
          )}

          {/* Post Tags */}
          {post.tags && post.tags.length > 0 && (
            <div className="flex flex-wrap gap-2 mb-4">
              {post.tags.map((tag, idx) => (
                <span
                  key={idx}
                  className="px-3 py-1 rounded-full text-sm"
                  style={{
                    backgroundColor: localTheme.accentColor + '30',
                    color: localTheme.textColor
                  }}
                >
                  #{tag}
                </span>
              ))}
            </div>
          )}

          {/* Post Actions */}
          <div className="flex items-center justify-between pt-4" style={{ borderTop: `1px solid ${localTheme.accentColor}20` }}>
            <div className="flex items-center gap-6">
              <button
                onClick={likePost}
                className="flex items-center gap-2 transition-colors"
                style={{ color: post.liked ? '#f87171' : localTheme.textColor + '99' }}
              >
                <Heart size={20} className={post.liked ? "fill-current" : ""} />
                <span>{post.likes}</span>
              </button>
              <button
                onClick={dislikePost}
                className="flex items-center gap-2 transition-colors"
                style={{ color: post.disliked ? '#f97316' : localTheme.textColor + '99' }}
              >
                <ThumbsDown size={20} className={post.disliked ? "fill-current" : ""} />
                <span>{post.dislikes || 0}</span>
              </button>
              <div className="flex items-center gap-2" style={{ color: localTheme.textColor + '99' }}>
                <span>{post.comments} comments</span>
              </div>
            </div>
            <button
              onClick={handleShare}
              className="flex items-center gap-2 transition-colors hover:text-blue-400"
              style={{ color: localTheme.textColor + '99' }}
            >
              <Share2 size={20} />
            </button>
          </div>
        </div>

        {/* Comment Input */}
        <div className="rounded-xl p-4 mb-6 backdrop-blur-sm border" style={{
          background: getCardBackground(localTheme as any, profileCustomization || {}, theme.cardBg),
          borderColor: localTheme.accentColor + '40'
        }}>
          <div className="flex gap-3">
            <div className="w-10 h-10 rounded-full flex items-center justify-center flex-shrink-0" style={{
              background: `linear-gradient(to bottom right, ${localTheme.accentColor}, ${localTheme.accentColor}dd)`
            }}>
              {(currentUser?.avatar || (currentUser as any)?.profilePicture) ? (
                <img src={resolveAvatar(currentUser?.avatar || (currentUser as any)?.profilePicture)} alt={currentUser?.displayName || currentUser?.username || 'User'} className="w-full h-full rounded-full object-cover" />
              ) : (
                <span style={{ color: localTheme.textColor }}>{currentUser?.displayName?.[0] || currentUser?.username?.[0] || 'U'}</span>
              )}
            </div>
            <div className="flex-1 flex gap-2">
              <input
                type="text"
                value={newComment}
                onChange={(e) => setNewComment(e.target.value)}
                onKeyPress={(e) => e.key === 'Enter' && handlePostComment()}
                placeholder="Write a comment..."
                className="flex-1 px-4 py-2 rounded-lg border outline-none transition-colors"
                style={{
                  backgroundColor: localTheme.accentColor + '10',
                  borderColor: localTheme.accentColor + '40',
                  color: localTheme.textColor
                }}
              />
              <button
                onClick={handlePostComment}
                className="px-4 py-2 rounded-lg transition-all hover:opacity-80"
                style={{
                  backgroundColor: localTheme.accentColor,
                  color: localTheme.textColor
                }}
              >
                <Send size={18} />
              </button>
            </div>
          </div>
        </div>

        {/* Comments Section */}
        <div className="space-y-4">
          <h3 className="text-xl font-bold mb-4" style={{ color: localTheme.textColor }}>
            Comments ({allComments.length})
          </h3>

          {displayedComments.map((comment) => (
            <div
              key={comment.id}
              className="rounded-xl p-4 backdrop-blur-sm border"
              style={{
                background: getCardBackground(localTheme as any, profileCustomization || {}, theme.cardBg),
                borderColor: localTheme.accentColor + '20'
              }}
            >
              <div className="flex gap-3">
                <Link to={comment.authorUsername ? `/user/${comment.authorUsername}` : '#'} className="w-10 h-10 rounded-full flex items-center justify-center flex-shrink-0 cursor-pointer transition-opacity hover:opacity-80" style={{
                  background: `linear-gradient(to bottom right, ${localTheme.accentColor}, ${localTheme.accentColor}dd)`
                }}>
                  {comment.authorAvatar ? (
                    <img src={comment.authorAvatar} alt={comment.author} className="w-full h-full rounded-full object-cover" />
                  ) : (
                    <span style={{ color: localTheme.textColor }}>{comment.author?.[0] || 'U'}</span>
                  )}
                </Link>
                <div className="flex-1">
                  <div className="flex flex-col gap-0.5 mb-1">
                    <div className="flex items-center gap-2">
                      <p className="font-semibold" style={{ color: localTheme.textColor }}>{comment.author || 'Unknown'}</p>
                      <span className="text-xs" style={{ color: localTheme.textColor + '66' }}>{formatTimestamp(comment.timestamp)}</span>
                    </div>
                    {comment.authorUsername && (
                      <p className="text-xs" style={{ color: localTheme.textColor + '99' }}>@{comment.authorUsername}</p>
                    )}
                  </div>
                  <p className="mb-2" style={{ color: localTheme.textColor + 'dd' }}>{comment.content}</p>
                  <div className="flex items-center gap-4">
                    <button
                      onClick={() => likeComment(comment.id)}
                      className="flex items-center gap-1 transition-colors"
                      style={{ color: comment.liked ? '#ef4444' : localTheme.textColor + '66' }}
                    >
                      <Heart size={14} className={comment.liked ? "fill-current" : ""} />
                      <span className="text-xs">{comment.likes}</span>
                    </button>
                    <button
                      onClick={() => dislikeComment(comment.id)}
                      className="flex items-center gap-1 transition-colors"
                      style={{ color: comment.disliked ? '#f97316' : localTheme.textColor + '66' }}
                    >
                      <ThumbsDown size={14} className={comment.disliked ? "fill-current" : ""} />
                      <span className="text-xs">{comment.dislikes}</span>
                    </button>
                  </div>
                </div>
              </div>
            </div>
          ))}

          {/* Load More Button */}
          {displayedComments.length < allComments.length && (
            <button
              onClick={loadMoreComments}
              className="w-full py-3 rounded-lg transition-all hover:opacity-80 flex items-center justify-center gap-2"
              style={{
                backgroundColor: localTheme.accentColor + '20',
                color: localTheme.textColor,
                border: `1px solid ${localTheme.accentColor}40`
              }}
            >
              Load More Comments
              <ChevronDown size={18} />
            </button>
          )}
        </div>
          </>
          </div>
        )}
      </div>

      {/* Edit Post Modal */}
      {editingPost && (
        <EditPostModal
          post={editingPost as any}
          isOpen={!!editingPost}
          onClose={() => setEditingPost(null)}
          onSave={handleSaveEdit}
        />
      )}

    </div>
  );
}
